import React, { useState, useEffect } from 'react'

import {
  Icon,
  Button,
  FavoriteOutlined,
  FavoriteFilled,
  VisibilityOn,
  Share,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { number, string } from 'prop-types'

import WishlistButton from 'Pods/ItemPage/Components/WishlistButton/WishlistButton'
import { usePreviewMode } from 'Pods/ItemPage/context/PreviewModeContext'
import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useSentry from 'Pods/Shared/Hooks/useSentry'
import i18n from 'Pods/Shared/I18n'
import SmartTooltip from 'Pods/Shared/SmartTooltip/SmartTooltip'

import formatNumber from './formatNumber'

// One shallow-compared slice: `/load-services.js` brings the user flags, `/products/:slug.js`
// brings `product`, and each event only re-renders this section when its own fields change.
const selectHeaderConfig = (config) => ({
  isLoggedIn: Boolean(config?.isCurrentUser),
  enableBuyNow: Boolean(config?.enableBuyNow),
  currentUserUsername: config?.currentUserUsername || '',
  product: config?.product,
})

let ShareModal
let CreditsAreaContent
let EmbedSection
let CustomizeEmbed
let Preview

function HeaderSection({ id, locale = 'en' }) {
  const [likes, setLikes] = useState(0)
  const [views, setViews] = useState(0)
  const [isLiked, setIsLiked] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false)
  const [isShareModalLoaded, setIsShareModalLoaded] = useState(false)
  const [modalTrigger, setModalTrigger] = useState(null)

  const { captureException } = useSentry()
  const { isPreview } = usePreviewMode()

  const { isLoggedIn, enableBuyNow, currentUserUsername, product } = usePageConfig({ select: selectHeaderConfig })

  const showWishlistInHeader = enableBuyNow

  const likeTooltipText = i18n.t('itemPage.headerSection.likeTooltip', { ns: 'rails', lng: locale })
  const viewsTooltipText = i18n.t('itemPage.headerSection.viewsTooltip', { ns: 'rails', lng: locale })
  const shareTooltipText = i18n.t('itemPage.headerSection.shareTooltip', { ns: 'rails', lng: locale })

  const designerName = product?.authorUsername || ''
  const productId = product?.id
  const is3dPrintModel = useIs3dPrintModel()
  const embedButtonLabel = `Embed this ${is3dPrintModel ? '3D Print Model' : '3D Model'}`

  const {
    getData: getValidLike,
    isLoading: isValidLikeLoading,
  } = useApiGet()

  const {
    postData: postLike,
    isLoading: isPostLikeLoading,
  } = useApiPost()


  useEffect(() => {
    if (shareModalIsOpen && !ShareModal && !isShareModalLoaded) {
      importShareModal()
    }
  }, [shareModalIsOpen, isShareModalLoaded])

  function importShareModal() {
    Promise.all([
      import('Pods/LazyLoaded/ShareModal/ShareModal'),
      import('Pods/LazyLoaded/ShareModal/Components/CreditsAreaContent'),
      import('Pods/LazyLoaded/ShareModal/Components/EmbedSection'),
      import('Pods/LazyLoaded/ShareModal/Components/CustomizeEmbed'),
      import('Pods/LazyLoaded/ShareModal/Components/Preview'),
    ])
      .then(([
        shareModalModule,
        creditsAreaContentModule,
        embedSectionModule,
        customizeEmbedModule,
        previewModule,
      ]) => {
        ShareModal = shareModalModule.default
        CreditsAreaContent = creditsAreaContentModule.default
        EmbedSection = embedSectionModule.default
        CustomizeEmbed = customizeEmbedModule.default
        Preview = previewModule.default
        setIsShareModalLoaded(true)
      })
      .catch((error) => {
        console.error('Failed to load ShareModal:', error)
      })
  }

  function handleMouseEnter() {
    if (!isShareModalLoaded && !ShareModal) {
      importShareModal()
    }
  }

  function handleTouchStart() {
    if (!isShareModalLoaded && !ShareModal) {
      importShareModal()
    }
  }

  useEffect(() => {
    // In preview mode, initialize with default values since pageConfig won't be available
    if (isPreview) {
      setLikes(0)
      setIsLiked(false)
      setViews(0)
      setIsInitialized(true)
      return
    }

    if (!product) {
      return
    }

    setLikes(product.likes || 0)
    setIsLiked(product.isLiked || false)
    setViews(product.views || 0)
    setIsInitialized(true)
  }, [product, isPreview])

  const handleShareClick = () => {
    setModalTrigger('share')
    setShareModalIsOpen(true)
  }

  const handleCloseShareModal = () => {
    setShareModalIsOpen(false)
    setModalTrigger(null)
  }

  const handleLikeClick = () => {
    if (!isLoggedIn) {
      App.events.publish('app:login:open')
      return
    }

    getValidLike({
      path: '/api/internal/likes/valid_like',
      onSuccess:(res) => {
        if (res.data === true) {
          const payload = {
            like: {
              likeable_id: id,
              likeable_type: 'Item',
            },
          }

          postLike({
            path: '/api/internal/likes/like',
            data: payload,
            onSuccess : () => {
              const wasLiked = isLiked
              setLikes(wasLiked ? likes - 1 : likes + 1)
              setIsLiked(!wasLiked)
              if (!wasLiked) {
                setModalTrigger('like')
                setShareModalIsOpen(true)
              }
            },
            onError: (err) => {
              captureException(err)
            },
          })
        }
      },
      onError: (err) => {
        captureException(err)
      },
    })
  }

  return (
    <>
      <div className={classnames(
        'details-area__header',
        showWishlistInHeader && 'details-area__header--with-wishlist'
      )}>
        {showWishlistInHeader && productId && (
          <div className="details-area__header-button-wrapper">
            <WishlistButton
              productId={productId}
              variant="compact"
              label="Save"
              savedLabel="Saved"
              disabled={isPreview}
              customClass="details-area__header-button"
            />
          </div>
        )}
        <div className="details-area__header-button-wrapper">
          <SmartTooltip
            content={likeTooltipText}
            id="like-item-tooltip"
            delayShow={100}
            delayHide={100}
            autoPosition
            containerSelector=".details-area__header"
          >
            <Button
              id="product-like-button"
              type="ghost"
              icon={isInitialized && <Icon svgComponent={isLiked ? FavoriteFilled : FavoriteOutlined} />}
              customClass={classnames(
                'details-area__header-button',
                (!isLoggedIn || isPreview) && 'details-area__header-button--is-disabled'
              )}
              onClick={handleLikeClick}
              disabled={!isInitialized || isValidLikeLoading || isPostLikeLoading || isPreview}
              aria-label={isLiked ? 'Unlike' : 'Like'}
            >
              {isInitialized && (formatNumber(likes))}
            </Button>
          </SmartTooltip>
        </div>
        <div className="details-area__views-wrapper">
            <SmartTooltip
              content={viewsTooltipText}
              id="views-item-tooltip"
              delayShow={100}
              delayHide={100}
              autoPosition
            >
              <div className="details-area__views">
                {isInitialized && (
                  <>
                    <Icon svgComponent={VisibilityOn} color="dark" className="details-area__views-icon" />
                    <span className="details-area__views-count">{formatNumber(views)}</span>
                  </>
                )}
              </div>
            </SmartTooltip>
        </div>
        <div className="details-area__header-button-wrapper">
          <SmartTooltip
            content={shareTooltipText}
            id="share-item-tooltip"
            delayShow={100}
            delayHide={100}
            autoPosition
          >
            <Button
              type="ghost"
              icon={isInitialized && <Icon svgComponent={Share} />}
              customClass="details-area__header-button"
              onClick={handleShareClick}
              onMouseEnter={handleMouseEnter}
              onTouchStart={handleTouchStart}
              disabled={isPreview}
              aria-label="Share"
            >
              {isInitialized && 'Share'}
            </Button>
          </SmartTooltip>
        </div>
      </div>
      {shareModalIsOpen && isShareModalLoaded && ShareModal && (
        <ShareModal
          closeModal={handleCloseShareModal}
          content={
            modalTrigger === 'like' && CreditsAreaContent ? (
              <CreditsAreaContent
                designerName={designerName}
                itemTitle={product?.title || ''}
              />
            ) : (
              <EmbedSection
                renderCustomize={(settings, onSettingsChange) => (
                  <CustomizeEmbed
                    embedUrl={`${window.location.origin}/embed/items/${id}`}
                    embedButtonLabel={embedButtonLabel}
                    sharerUsername={currentUserUsername}
                    settings={settings}
                    onSettingsChange={onSettingsChange}
                  />
                )}
                renderPreview={(settings) => (
                  <Preview
                    embedUrl={`${window.location.origin}/embed/items/${id}`}
                    sharerUsername={currentUserUsername}
                    settings={settings}
                  />
                )}
              />
            )
          }
        />
      )}
    </>
  )
}

HeaderSection.propTypes = {
  id: number.isRequired,
  locale: string,
}

export default HeaderSection
